import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '@/components/card/card';
import { DataGrid } from '@/components/data-grid';
import { createColumnHelper } from '@tanstack/react-table';
import { IRole } from '@/types/roles-permissions';
import { KeenIcon } from '@/components';
import { RolesService } from '@/services/roles/rolesService';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import usePagination from '@/hooks/usePagination';
import { formatDisplayDate } from '@/utils/Date';

interface RolesListProps {
  onEditRole: (role: IRole) => void;
  onDeleteRole: (role: IRole) => void;
  refreshTrigger?: number;
}

const RolesList: React.FC<RolesListProps> = ({
  onEditRole,
  onDeleteRole,
  refreshTrigger,
}) => {
  const [roles, setRoles] = useState<IRole[]>([]);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [dataKey, setDataKey] = useState(() => Date.now());

  const {
    totalItems,
    handleTotalItems,
    totalPages,
    handleTotalPages,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
  } = usePagination();

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageSize: 5,
    }));
  }, [setPagination]);

  const columnHelper = createColumnHelper<IRole>();

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('display_name', {
        header: 'Role Name',
        cell: (info) => (
          <div className="font-medium text-gray-900">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => <div className="text-gray-600">{info.getValue()}</div>,
      }),
      columnHelper.accessor('updated_at', {
        header: 'Last Updated',
        cell: (info) => {
          const date = info.getValue();
          return date ? (
            <div className="text-gray-600">
              {formatDisplayDate(new Date(date))}
            </div>
          ) : null;
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <div className="flex space-x-2">
            <button
              className="btn btn-sm btn-icon btn-light btn-outline"
              onClick={() => handleEditPermissions(info.row.original)}
              title="Edit Permissions"
            >
              <KeenIcon type="filled" icon="key" className="text-gray-500" />
            </button>
            <button
              className="btn btn-sm btn-icon btn-light btn-outline"
              aria-label={`Edit ${info.row.original.name}`}
              onClick={() => onEditRole(info.row.original)}
              title="Edit Role"
            >
              <KeenIcon type="filled" icon="pencil" className="text-gray-500" />
            </button>
            <button
              className="btn btn-sm btn-icon btn-light btn-outline"
              title="Delete Role"
              onClick={() => onDeleteRole(info.row.original)}
            >
              <KeenIcon type="filled" icon="trash" className="text-red-500" />
            </button>
          </div>
        ),
      }),
    ],
    [columnHelper, onEditRole, onDeleteRole]
  );

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await RolesService.getRoles(
        pagination.pageIndex + 1,
        pagination.pageSize,
        debouncedValue
      );

      if (response.roles && Array.isArray(response.roles)) {
        const transformedRoles: IRole[] = response.roles.map(
          (role: unknown) => {
            const r = role as {
              id?: string;
              name?: string;
              display_name?: string;
              description?: string;
              updated_at?: string;
              has_admin_panel_access?: boolean;
              has_user_front_access?: boolean;
            };
            return {
              id: r.id || '',
              name: r.name || '',
              display_name: r.display_name || '',
              description: r.description || '',
              updated_at: r.updated_at || new Date().toISOString(),
              has_admin_panel_access: r.has_admin_panel_access ?? true,
              has_user_front_access: r.has_user_front_access ?? false,
            };
          }
        );

        setRoles(transformedRoles);
        handleTotalItems(response.totalCount || response.roles.length);
        const calculatedTotalPages = Math.ceil(
          (response.totalCount || response.roles.length) / pagination.pageSize
        );
        handleTotalPages(calculatedTotalPages);
      } else {
        console.error('Invalid roles data format:', response);
        enqueueSnackbar('Invalid data format received from server', {
          variant: 'error',
        });
        setRoles([]);
      }
    } catch (error: unknown) {
      console.error('Error fetching roles:', error);
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.message || err?.message || 'Failed to load roles';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    debouncedValue,
    enqueueSnackbar,
    handleTotalItems,
    handleTotalPages,
  ]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      fetchRoles();
    }
  }, [refreshTrigger, fetchRoles]);

  const fetchRolesRef = useRef(fetchRoles);

  useEffect(() => {
    fetchRolesRef.current = fetchRoles;
  }, [fetchRoles]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchRolesRef.current();
        setDataKey(Date.now());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Remove fetchRoles from dependencies

  const handleEditPermissions = (role: IRole) => {
    navigate(`/roles-management/permissions/${role.id}`);
  };

  return (
    <Card className="overflow-hidden border-1 shadow-[0px 3px 4px 0px rgba(0, 0, 0, 0.03)] bg-gray-100 border-gray-200 border rounded-[0.75rem]">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="card-title text-lg font-semibold text-gray-900">
          Roles
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0">
            <label className="input input-sm h-8 sm:h-10 flex items-center">
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                placeholder="Search roles..."
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search roles"
              />
            </label>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="text-center py-4" role="status" aria-live="polite">
          Loading roles...
        </div>
      ) : roles?.length === 0 ? (
        <div className="text-center py-4" role="alert">
          No roles found
        </div>
      ) : (
        <DataGrid
          key={dataKey}
          columns={columns}
          data={roles}
          loading={loading}
          rowSelect={false}
          pagination={{
            page: pagination.pageIndex,
            size: pagination.pageSize,
            more: true,
            totalItems: totalItems,
            totalPages: totalPages,
            setPagination,
            info: `{from} - {to} of {count}`,
            sizes: [5, 10, 25, 50, 100],
            sizesLabel: 'Show',
            sizesDescription: 'per page',
          }}
          serverSide={true}
        />
      )}
    </Card>
  );
};

export default React.memo(RolesList);
